/**
 * LargeBondsPage - Main page for large bond management
 * Open/Closed tabs, status filtering, card list with actions
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  PlusIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  XCircleIcon,
  ChevronDownIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  UserIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathAgencySelector,
  OathAgencyInfo,
  OathBanner,
  OathEmptyState,
  OathModal,
  OathButton,
  OathInput,
} from '../../shared/OathUI';
import { getLargeBonds, getLargeBondDetails, cancelLargeBond } from '../../../services/oathLargeBondService';
import { STATUS_LABELS, STATUS_COLORS, STATUS_BORDER_COLORS, OPEN_STATUSES, CLOSED_STATUSES } from './shared/largeBondConstants';
import { formatCurrency, formatDate } from '../transferbonds/shared/formatUtils';
import { Pagination } from '../powerreporting/components/shared/TableComponents';
import CreateLargeBondModal from './CreateLargeBondModal';
import LargeBondDetailModal from './LargeBondDetailModal';

const DEFAULT_PAGE_SIZE = 10;

const STATUS_FILTERS = [
  { value: 'ALL', label: 'All' },
  { value: 'AP', label: 'Approved' },
  { value: 'PD', label: 'Pending' },
];

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const LargeBondsPage = () => {
  const { user } = useOathAuth();
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);

  // Agency selection
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  useEffect(() => {
    if (agencies.length > 0 && !selectedAgencyId) {
      const mainAgency = agencies.find((a) => a.isMainAgency);
      setSelectedAgencyId((mainAgency || agencies[0]).agencyId);
    }
  }, [agencies, selectedAgencyId]);

  // Bond data
  const [bonds, setBonds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tab & filters
  const [activeTab, setActiveTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [appliedDateRange, setAppliedDateRange] = useState({ from: '', to: '' });
  const [hasSearchedClosed, setHasSearchedClosed] = useState(false);

  // Pagination
  const [openPage, setOpenPage] = useState(0);
  const [openPageSize, setOpenPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [closedPage, setClosedPage] = useState(0);
  const [closedPageSize, setClosedPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sticky footer
  const [isFooterFixed, setIsFooterFixed] = useState(false);
  const [fixedFooterStyle, setFixedFooterStyle] = useState({});
  const contentCardRef = useRef(null);
  const checkFooterRef = useRef(null);

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState('create');
  const [editBondId, setEditBondId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewBond, setViewBond] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [bondToCancel, setBondToCancel] = useState(null);

  // Fetch bonds
  const fetchBonds = useCallback(async () => {
    if (!selectedAgencyId) return;
    const result = await getLargeBonds(selectedAgencyId);
    if (result.success) {
      // Deduplicate by largeBondId in case backend returns duplicates
      const seen = new Set();
      const unique = (result.data || []).filter((b) => {
        if (seen.has(b.largeBondId)) return false;
        seen.add(b.largeBondId);
        return true;
      });
      setBonds(unique);
    }
    setLoading(false);
  }, [selectedAgencyId]);

  // Initial fetch + 15s polling
  useEffect(() => {
    fetchBonds();
    const interval = setInterval(fetchBonds, 15000);
    return () => clearInterval(interval);
  }, [fetchBonds]);

  // Pause polling when tab not visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchBonds();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchBonds]);

  // Filter bonds
  const filteredBonds = useMemo(() => {
    const statusList = activeTab === 0 ? OPEN_STATUSES : CLOSED_STATUSES;

    return bonds
      .filter((bond) => {
        let matchesStatus = statusList.includes(bond.status);

        // Open tab status sub-filter
        if (activeTab === 0 && statusFilter !== 'ALL') {
          matchesStatus = bond.status === statusFilter;
        }

        // Closed tab date range filter
        if (activeTab === 1) {
          const createdDate = new Date(bond.dateCreated);
          // 6-month max window
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          if (createdDate < sixMonthsAgo) return false;

          if (appliedDateRange.from) {
            if (createdDate < new Date(appliedDateRange.from)) return false;
          }
          if (appliedDateRange.to) {
            if (createdDate > new Date(appliedDateRange.to)) return false;
          }
        }

        return matchesStatus;
      })
      .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated) || b.largeBondId - a.largeBondId);
  }, [bonds, activeTab, statusFilter, appliedDateRange]);

  // Clamp page to valid range when data changes (only for the active tab)
  useEffect(() => {
    if (activeTab === 0) {
      const maxPage = Math.max(0, Math.ceil(filteredBonds.length / openPageSize) - 1);
      if (openPage > maxPage) setOpenPage(maxPage);
    } else {
      const maxPage = Math.max(0, Math.ceil(filteredBonds.length / closedPageSize) - 1);
      if (closedPage > maxPage) setClosedPage(maxPage);
    }
  }, [activeTab, filteredBonds.length, openPageSize, openPage, closedPageSize, closedPage]);

  // Paginated bonds for current tab
  const paginatedBonds = useMemo(() => {
    const pg = activeTab === 0 ? openPage : closedPage;
    const sz = activeTab === 0 ? openPageSize : closedPageSize;
    const maxPage = Math.max(0, Math.ceil(filteredBonds.length / sz) - 1);
    const safePage = Math.min(pg, maxPage);
    const start = safePage * sz;
    return filteredBonds.slice(start, start + sz);
  }, [filteredBonds, activeTab, openPage, openPageSize, closedPage, closedPageSize]);

  // Reset page when filters change
  useEffect(() => { setOpenPage(0); }, [statusFilter]);
  useEffect(() => { setClosedPage(0); }, [appliedDateRange]);

  // Sticky footer — scroll listener checks if card bottom is below viewport
  useEffect(() => {
    const container = contentCardRef.current;
    if (!container) return;

    const isDesktop = () => window.innerWidth >= 1024;

    const checkFooter = () => {
      if (!isDesktop()) {
        setIsFooterFixed(false);
        return;
      }
      const rect = container.getBoundingClientRect();
      const needsFixed = rect.bottom > window.innerHeight + 20;
      setIsFooterFixed(needsFixed);
      if (needsFixed) {
        setFixedFooterStyle({
          position: 'fixed',
          bottom: '0',
          left: rect.left,
          width: rect.width,
          zIndex: 20,
        });
      }
    };

    checkFooterRef.current = checkFooter;
    checkFooter();
    window.addEventListener('scroll', checkFooter, { passive: true });
    window.addEventListener('resize', checkFooter, { passive: true });
    return () => {
      window.removeEventListener('scroll', checkFooter);
      window.removeEventListener('resize', checkFooter);
    };
  }, [activeTab, filteredBonds.length]);

  // Re-check footer after page/pageSize changes (content height changes without scroll)
  useEffect(() => {
    requestAnimationFrame(() => checkFooterRef.current?.());
  }, [openPage, openPageSize, closedPage, closedPageSize]);

  // Actions
  const handleEdit = (bond) => {
    setCreateMode('edit');
    setEditBondId(bond.largeBondId);
    setCreateModalOpen(true);
  };

  const handleClone = (bond) => {
    setCreateMode('clone');
    setEditBondId(bond.largeBondId);
    setCreateModalOpen(true);
  };

  const handleView = async (bond) => {
    const result = await getLargeBondDetails(bond.largeBondId);
    if (result.success) {
      setViewBond(result.data);
      setViewOpen(true);
    } else {
      toast.error('Failed to load bond details');
    }
  };

  const handleCancelClick = (bond) => {
    setBondToCancel(bond);
    setCancelConfirmOpen(true);
  };

  const confirmCancel = async () => {
    if (!bondToCancel) return;
    const result = await cancelLargeBond(bondToCancel.largeBondId);
    if (result.success) {
      toast.success(`Bond ${bondToCancel.refNumber} cancelled`);
      setBonds((prev) =>
        prev.map((b) =>
          b.largeBondId === bondToCancel.largeBondId ? { ...b, status: 'NW' } : b
        )
      );
    } else {
      toast.error('Failed to cancel bond');
    }
    setCancelConfirmOpen(false);
    setBondToCancel(null);
  };

  const handleCreateNew = () => {
    setCreateMode('create');
    setEditBondId(null);
    setCreateModalOpen(true);
  };

  // Status counts for filter pills
  const statusCounts = useMemo(() => {
    const openBonds = bonds.filter((b) => OPEN_STATUSES.includes(b.status));
    return {
      ALL: openBonds.length,
      AP: openBonds.filter((b) => b.status === 'AP').length,
      PD: openBonds.filter((b) => b.status === 'PD').length,
    };
  }, [bonds]);

  const selectedAgency = agencies.find((a) => a.agencyId === selectedAgencyId);

  const tabClass = ({ selected }) =>
    `flex-1 py-4 text-center text-sm font-medium transition-colors focus:outline-none ${
      selected
        ? 'bg-white dark:bg-gray-800 text-primary font-semibold border-b-2 border-primary'
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;

  return (
    <OathPageWrapper maxWidth="max-w-7xl">
      {/* Header */}
      <OathHeaderCard
        title="Large Bonds"
        subtitle="Submit and manage large bond requests"
        icon={BuildingLibraryIcon}
        compact
        rightContent={
          <OathAgencySelector
            agencies={agencies}
            selectedAgencyId={selectedAgencyId}
            onSelect={setSelectedAgencyId}
          />
        }
      >
        {agencies.length === 1 && <OathAgencyInfo agency={selectedAgency} />}
      </OathHeaderCard>

      {/* Main Content */}
      <div ref={contentCardRef}>
      <OathContentCard noPadding>
        <TabGroup selectedIndex={activeTab} onChange={setActiveTab}>
          <TabList className="flex border-b border-primary-100 dark:border-gray-700 bg-primary-50/50 dark:bg-gray-800/50">
            <Tab className={tabClass}>Open Bonds</Tab>
            <Tab className={tabClass}>Closed Bonds</Tab>
          </TabList>

          <TabPanels>
            {/* Open Bonds Tab */}
            <TabPanel className="p-4 sm:p-6">
              <OathBanner
                message="Submit a new large bond request for compliance review."
                actionLabel="New Bond"
                actionIcon={PlusIcon}
                onAction={handleCreateNew}
              />

              {/* Status filter + count */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  {STATUS_FILTERS.map((f) => {
                    const count = statusCounts[f.value] ?? 0;
                    return (
                      <button
                        key={f.value}
                        onClick={() => setStatusFilter(f.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          statusFilter === f.value
                            ? 'bg-primary text-white shadow-sm'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {f.label}
                        <span className={`ml-1.5 ${statusFilter === f.value ? 'text-white/80' : 'text-gray-400 dark:text-gray-500'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {!loading && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {filteredBonds.length} bond{filteredBonds.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              <BondList
                bonds={paginatedBonds}
                loading={loading}
                showActions
                onEdit={handleEdit}
                onClone={handleClone}
                onView={handleView}
                onCancel={handleCancelClick}
              />

              {/* Pagination — hidden when fixed clone is active */}
              <div className={`mt-6 ${isFooterFixed ? 'invisible' : ''}`}>
                <Pagination
                  page={openPage}
                  pageSize={openPageSize}
                  totalCount={filteredBonds.length}
                  onPageChange={setOpenPage}
                  onPageSizeChange={(size) => { setOpenPageSize(size); setOpenPage(0); }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </div>
            </TabPanel>

            {/* Closed Bonds Tab */}
            <TabPanel className="p-4 sm:p-6">
              {/* Date range filter */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
                <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
                  <OathInput
                    label="From"
                    type="date"
                    value={dateRange.from}
                    onChange={(val) => setDateRange((prev) => ({ ...prev, from: val }))}
                  />
                  <OathInput
                    label="To"
                    type="date"
                    value={dateRange.to}
                    onChange={(val) => setDateRange((prev) => ({ ...prev, to: val }))}
                  />
                </div>
                <div className="flex gap-2">
                  <OathButton
                    onClick={() => { setAppliedDateRange({ ...dateRange }); setHasSearchedClosed(true); }}
                    disabled={!dateRange.from && !dateRange.to}
                    className="flex-1 sm:flex-none"
                  >
                    Search
                  </OathButton>
                  {hasSearchedClosed && (
                    <OathButton
                      variant="outline"
                      onClick={() => {
                        setDateRange({ from: '', to: '' });
                        setAppliedDateRange({ from: '', to: '' });
                        setHasSearchedClosed(false);
                      }}
                      className="flex-1 sm:flex-none"
                    >
                      Clear
                    </OathButton>
                  )}
                </div>
              </div>

              {/* Search result feedback */}
              {hasSearchedClosed && !loading && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  {filteredBonds.length > 0
                    ? `Found ${filteredBonds.length} closed bond${filteredBonds.length !== 1 ? 's' : ''} for ${appliedDateRange.from || 'any'}${appliedDateRange.to ? ` to ${appliedDateRange.to}` : ''}`
                    : null}
                </p>
              )}

              <BondList
                bonds={paginatedBonds}
                loading={loading}
                onView={handleView}
                emptyTitle={hasSearchedClosed ? 'No results' : 'No closed bonds'}
                emptyMessage={hasSearchedClosed
                  ? 'No closed bonds found for the selected date range. Try adjusting your dates.'
                  : 'Select a date range and click Search to find closed bonds.'}
              />

              {/* Pagination — hidden when fixed clone is active */}
              <div className={`mt-6 ${isFooterFixed ? 'invisible' : ''}`}>
                <Pagination
                  page={closedPage}
                  pageSize={closedPageSize}
                  totalCount={filteredBonds.length}
                  onPageChange={setClosedPage}
                  onPageSizeChange={(size) => { setClosedPageSize(size); setClosedPage(0); }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </div>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </OathContentCard>
      </div>

      {/* Fixed pagination footer — visible when original scrolls out of view (desktop only) */}
      {isFooterFixed && (
        <div
          style={fixedFooterStyle}
          className="hidden lg:block bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)] rounded-b-xl px-6"
        >
          <Pagination
            page={activeTab === 0 ? openPage : closedPage}
            pageSize={activeTab === 0 ? openPageSize : closedPageSize}
            totalCount={filteredBonds.length}
            onPageChange={activeTab === 0 ? setOpenPage : setClosedPage}
            onPageSizeChange={
              activeTab === 0
                ? (size) => { setOpenPageSize(size); setOpenPage(0); }
                : (size) => { setClosedPageSize(size); setClosedPage(0); }
            }
            pageSizeOptions={[10, 25, 50]}
            className="!mt-0 !pt-3 !pb-3 !border-t-0"
          />
        </div>
      )}

      {/* Create/Edit/Clone Modal */}
      <CreateLargeBondModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditBondId(null);
        }}
        onSaved={fetchBonds}
        mode={createMode}
        bondId={editBondId}
        agencyId={selectedAgencyId}
      />

      {/* Detail Modal */}
      <LargeBondDetailModal
        isOpen={viewOpen}
        onClose={() => setViewOpen(false)}
        bond={viewBond}
        agencyId={selectedAgencyId}
      />

      {/* Cancel Confirmation */}
      <OathModal
        isOpen={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        title="Confirm Cancellation"
        size="sm"
        footer={
          <div className="flex gap-3 ml-auto">
            <OathButton variant="outline" onClick={() => setCancelConfirmOpen(false)}>
              No
            </OathButton>
            <OathButton variant="danger" onClick={confirmCancel}>
              Yes, Cancel Bond
            </OathButton>
          </div>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Are you sure you want to cancel bond <span className="font-semibold text-gray-900 dark:text-white">{bondToCancel?.refNumber}</span>?
          This action cannot be undone.
        </p>
      </OathModal>
    </OathPageWrapper>
  );
};

// ================================================================
// Bond Card List
// ================================================================

const SkeletonCard = () => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 animate-pulse">
    <div className="flex items-center gap-3">
      <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full" />
      <div className="h-4 flex-1 bg-gray-200 dark:bg-gray-700 rounded max-w-[200px]" />
      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded hidden sm:block" />
      <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded hidden md:block" />
      <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  </div>
);

const BondList = ({ bonds, loading, showActions = false, onEdit, onClone, onView, onCancel, emptyTitle, emptyMessage }) => {
  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  if (bonds.length === 0) {
    return (
      <OathEmptyState
        title={emptyTitle || 'No bonds found'}
        message={emptyMessage || 'No large bonds match your current filters.'}
      />
    );
  }

  return (
    <div className="space-y-2">
      {bonds.map((bond, index) => (
        <BondCard
          key={bond.largeBondId}
          bond={bond}
          index={index}
          showActions={showActions}
          onEdit={onEdit}
          onClone={onClone}
          onView={onView}
          onCancel={onCancel}
        />
      ))}
    </div>
  );
};

const BondCard = ({ bond, index, showActions, onEdit, onClone, onView, onCancel }) => {
  const [expanded, setExpanded] = useState(false);
  const statusLabel = STATUS_LABELS[bond.status] || bond.status;
  const statusColor = STATUS_COLORS[bond.status] || 'bg-gray-100 text-gray-800';
  const borderColor = STATUS_BORDER_COLORS[bond.status] || 'border-l-gray-300';
  const canEdit = OPEN_STATUSES.includes(bond.status);
  const canCancel = bond.status === 'PD';
  const defendantName = bond.defendantName || [bond.firstName, bond.lastName].filter(Boolean).join(' ') || 'Unknown';
  const isOdd = index % 2 === 1;

  return (
    <div className={`border border-gray-200 dark:border-gray-700 border-l-[3px] ${borderColor} rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ${
      isOdd ? 'bg-gray-50/60 dark:bg-gray-800/80' : 'bg-white dark:bg-gray-800'
    }`}>
      {/* Collapsed summary row — always visible */}
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer group"
      >
        {/* Status badge */}
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusColor}`}>
          {statusLabel}
        </span>

        {/* Defendant name */}
        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-white truncate">
          {defendantName}
        </span>

        {/* Amount */}
        <span className="hidden sm:inline text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
          {formatCurrency(bond.amount)}
        </span>

        {/* Relative time */}
        <span className="hidden md:inline text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap" title={formatDate(bond.dateCreated)}>
          {timeAgo(bond.dateCreated)}
        </span>

        {/* Ref # */}
        <span className="hidden lg:inline text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
          Ref# {bond.refNumber}
        </span>

        {/* Chevron */}
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-transform duration-200 flex-shrink-0 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4">
          {/* Divider */}
          <div className="border-t border-gray-100 dark:border-gray-700 mb-3" />

          {/* Detail grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <DetailField icon={CurrencyDollarIcon} label="Bond Amount" value={formatCurrency(bond.amount)} />
            <DetailField icon={UserIcon} label="Defendant" value={defendantName} />
            <DetailField icon={CalendarDaysIcon} label="Date Created" value={formatDate(bond.dateCreated)} />
            <DetailField icon={BuildingOfficeIcon} label="Agency" value={bond.agencyName || 'N/A'} />
            {bond.requestedBy && (
              <DetailField label="Requested By" value={bond.requestedBy} />
            )}
            {bond.refNumber && (
              <DetailField label="Ref #" value={bond.refNumber} />
            )}
          </div>

          {/* Action buttons */}
          {(showActions || onView) && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              {onView && (
                <ActionButton icon={EyeIcon} label="View" onClick={() => onView(bond)} color="primary" />
              )}
              {showActions && (
                <>
                  {canEdit && onEdit && (
                    <ActionButton icon={PencilSquareIcon} label="Edit" onClick={() => onEdit(bond)} color="primary" />
                  )}
                  {onClone && (
                    <ActionButton icon={DocumentDuplicateIcon} label="Clone" onClick={() => onClone(bond)} color="success" />
                  )}
                  {canCancel && onCancel && (
                    <ActionButton icon={XCircleIcon} label="Cancel" onClick={() => onCancel(bond)} color="danger" />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DetailField = ({ icon: Icon, label, value }) => (
  <div className="min-w-0">
    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5 flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </p>
    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{value}</p>
  </div>
);

const ACTION_COLORS = {
  primary: 'text-primary hover:bg-primary-50 dark:hover:bg-primary-900/20',
  success: 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20',
  danger: 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20',
};

const ActionButton = ({ icon: Icon, label, onClick, color = 'primary' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    title={label}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${ACTION_COLORS[color] || ACTION_COLORS.primary}`}
  >
    <Icon className="h-4 w-4" />
    <span className="hidden sm:inline">{label}</span>
  </button>
);

export default LargeBondsPage;
